import React, { useState } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD;

const EmailComposer = ({ employeeId, companyUID, employeeName, employeeEmail, onClose, onSuccess }) => {
  const { currentUser } = useAuth();
  const { t } = useTranslation(['myteam', 'common']);
  const { isRTL } = useLanguage();
  const [to, setTo] = useState(employeeEmail || '');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!to.trim()) {
      alert(t('drawerModals.email.errors.to', 'Please enter a recipient email address'));
      return;
    }
    if (!subject.trim()) {
      alert(t('drawerModals.email.errors.subject', 'Please enter a subject'));
      return;
    }
    if (!body.trim()) {
      alert(t('drawerModals.email.errors.message', 'Please enter a message body'));
      return;
    }

    setSending(true);
    try {
      const response = await fetch(`${BACKEND_URL}/employee/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyUID,
          employeeId,
          to: to.trim(),
          subject: subject.trim(),
          body: body.trim(),
          cc: cc.trim() || undefined,
          bcc: bcc.trim() || undefined,
          fromUID: currentUser?.uid,
          fromName: currentUser?.displayName || currentUser?.email || 'HR Team',
          fromEmail: currentUser?.email
        })
      });

      const result = await response.json();
      if (result.success) {
        alert(t('drawerModals.email.success', 'Email sent successfully'));
        onSuccess?.();
        onClose();
      } else {
        alert(t('drawerModals.email.failureWithReason', {
          message: result.error || t('drawerModals.email.failure', 'Failed to send email')
        }));
      }
    } catch (error) {
      console.error('Send email failed:', error);
      alert(t('drawerModals.email.failure', 'Failed to send email'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '700px',
          direction: isRTL ? 'rtl' : 'ltr',
          textAlign: isRTL ? 'right' : 'left'
        }}
      >
        <div className="modal-header">
          <h2>{t('drawerModals.email.title', 'Send Email')}</h2>
          <button className="close" onClick={onClose}>&times;</button>
        </div>
        <div className="form-section">
          <div className="input-group">
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--app-heading, #031128)'
            }}>
              {t('drawerModals.email.to', 'To')} <span style={{ color: 'var(--app-tone-eb4040, #EB4040)' }}>*</span>
            </label>
            <input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder={t('drawerModals.email.placeholders.to', 'employee@example.com')}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #E0E0E0',
                borderRadius: '8px',
                fontFamily: 'Montserrat',
                fontSize: '16px',
                color: 'var(--app-heading, #031128)'
              }}
            />
            {employeeName && (
              <div style={{
                marginTop: '4px',
                fontFamily: 'Montserrat',
                fontSize: '12px',
                color: '#666'
              }}>
                {employeeName}
              </div>
            )}
          </div>

          <div className="input-group">
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--app-heading, #031128)'
            }}>
              {t('drawerModals.email.cc', 'CC (Optional)')}
            </label>
            <input
              type="text"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              placeholder={t('drawerModals.email.placeholders.cc', 'cc@example.com')}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #E0E0E0',
                borderRadius: '8px',
                fontFamily: 'Montserrat',
                fontSize: '16px',
                color: 'var(--app-heading, #031128)'
              }}
            />
          </div>

          <div className="input-group">
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--app-heading, #031128)'
            }}>
              {t('drawerModals.email.bcc', 'BCC (Optional)')}
            </label>
            <input
              type="text"
              value={bcc}
              onChange={(e) => setBcc(e.target.value)}
              placeholder={t('drawerModals.email.placeholders.bcc', 'bcc@example.com')}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #E0E0E0',
                borderRadius: '8px',
                fontFamily: 'Montserrat',
                fontSize: '16px',
                color: 'var(--app-heading, #031128)'
              }}
            />
          </div>

          <div className="input-group">
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--app-heading, #031128)'
            }}>
              {t('drawerModals.email.subject', 'Subject')} <span style={{ color: 'var(--app-tone-eb4040, #EB4040)' }}>*</span>
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={t('drawerModals.email.placeholders.subject', 'Email subject')}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #E0E0E0',
                borderRadius: '8px',
                fontFamily: 'Montserrat',
                fontSize: '16px',
                color: 'var(--app-heading, #031128)'
              }}
            />
          </div>

          <div className="input-group">
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--app-heading, #031128)'
            }}>
              {t('drawerModals.email.message', 'Message')} <span style={{ color: 'var(--app-tone-eb4040, #EB4040)' }}>*</span>
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={t('drawerModals.email.placeholders.message', 'Type your message here...')}
              style={{
                width: '100%',
                minHeight: '200px',
                padding: '12px 16px',
                border: '2px solid #E0E0E0',
                borderRadius: '8px',
                fontFamily: 'Montserrat',
                fontSize: '14px',
                color: 'var(--app-heading, #031128)',
                resize: 'vertical'
              }}
            />
          </div>
        </div>
        <div className="modal-footer" style={{
          padding: '20px 32px',
          borderTop: '1px solid #E0E0E0',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          flexDirection: isRTL ? 'row-reverse' : 'row'
        }}>
          <button
            className="btn-secondary"
            onClick={onClose}
            disabled={sending}
            style={{
              padding: '12px 24px',
              backgroundColor: '#F8F9FA',
              border: '1px solid #E0E0E0',
              borderRadius: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: "var(--app-neutral-ink, #031128)",
              cursor: sending ? 'not-allowed' : 'pointer'
            }}
            data-light-control={"quiet"}>
            {t('common:buttons.cancel', 'Cancel')}
          </button>
          <button
            className="btn-primary"
            onClick={handleSend}
            disabled={sending || !to.trim() || !subject.trim() || !body.trim()}
            style={{ '--light-control-fill': sending || !to.trim() || !subject.trim() || !body.trim() ? '#CCCCCC' : '#568ded',
              padding: '12px 24px',
              backgroundColor: sending || !to.trim() || !subject.trim() || !body.trim() ? '#CCCCCC' : '#568ded',
              border: 'none',
              borderRadius: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: '#FFFFFF',
              cursor: sending || !to.trim() || !subject.trim() || !body.trim() ? 'not-allowed' : 'pointer'
            }}
            data-light-control={sending || !to.trim() || !subject.trim() || !body.trim() ? "quiet" : "filled"}>
            {sending ? t('drawerModals.email.sending', 'Sending...') : t('drawerModals.email.send', 'Send Email')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailComposer;
